import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword, sendEmailVerification, signOut } from 'firebase/auth';
import axios from 'axios';
import { auth } from '../firebase';
import { API_BASE } from '../lib/runtimeEnv';
import SphereOrb from './SphereOrb';
import SocialAuth from './SocialAuth';
import '../Dashboard.css';

const API_URL = API_BASE;

function messageFor(code) {
  switch (code) {
    case 'user_exists': return 'An account with that email already exists. Try signing in instead.';
    case 'birth_year_required': return 'Enter the year you were born.';
    case 'auth/invalid-email': return 'That email doesn’t look right.';
    case 'auth/weak-password': return 'Password needs at least 6 characters.';
    case 'auth/network-request-failed': return 'Network error. Check your connection.';
    default: return 'Could not create your account. Please try again.';
  }
}

// Web registration — same backend flow as the app (POST /auth/register, then an email
// activation link). Friends who arrive via a circle invite link land here, and the
// invite is re-applied after they verify and sign in.
export default function Register() {
  const location = useLocation();
  // Where to send them after sign-in — typically the /join/<code> page that brought them.
  const dest = location.state?.from?.pathname || '/app';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [birthYear, setBirthYear] = useState('');
  // Show/hide toggles (parity with the app's create-account screen).
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [existing, setExisting] = useState(false); // email already registered → offer sign-in
  // Age-neutral gate outcome: the server said this signup needs a guardian instead.
  const [needsGuardian, setNeedsGuardian] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null); setExisting(false); setNeedsGuardian(false);
    if (password !== confirm) { setError('Passwords don’t match.'); return; }
    setBusy(true);
    try {
      // The backend creates the Firebase user only after validating the birth year, so a
      // rejected signup leaves nothing behind — no client-side cleanup needed.
      await axios.post(`${API_URL}/auth/register`, { email: email.trim(), password, birthYear: Number(birthYear) });
      // Sign in just long enough to send the activation link, mirroring the app's flow.
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      await sendEmailVerification(cred.user);
      // Sign out so an unverified account can't slip into /app via the still-live session — matches
      // the Flutter controller (which signs out after sending the link) and the new RequireAuth gate.
      // The stashed invite/referral code lives in localStorage and survives this, so it still redeems
      // after they verify and sign in. (QA P1-4)
      await signOut(auth);
      setSent(true);
    } catch (err) {
      const code = err?.response?.data?.error || err?.code;
      if (code === 'underage_requires_guardian') {
        // Friendly hand-off, not an error banner: a parent or guardian can add them as a
        // managed member from the guardian's own account.
        setNeedsGuardian(true);
      } else {
        setError(messageFor(code));
      }
      // Already-registered email → surface a one-tap path to sign in (breaks the invite re-create loop).
      setExisting(code === 'user_exists' || code === 'auth/email-already-in-use');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="db-auth-page">
      <div className="db-auth-bg" aria-hidden="true">
        <span className="db-glow db-glow-a" />
        <span className="db-glow db-glow-b" />
      </div>
      {sent ? (
        <div className="db-auth-card">
          <div className="db-auth-hero">
            <SphereOrb size={108} />
            <Link to="/" className="db-auth-brand">Sphere<span className="db-grad">Sync</span></Link>
          </div>
          <h1 className="db-auth-title">Check your email</h1>
          <p className="db-auth-sub">
            We sent an activation link to <b>{email}</b>. Tap it, then sign in to finish up.
          </p>
          <Link
            to="/login"
            state={{ from: { pathname: dest } }}
            className="db-btn db-btn-primary db-btn-block"
            style={{ textAlign: 'center' }}
          >
            Go to sign in
          </Link>
        </div>
      ) : (
        <form className="db-auth-card" onSubmit={onSubmit}>
          <div className="db-auth-hero">
            <SphereOrb size={108} />
            <Link to="/" className="db-auth-brand">Sphere<span className="db-grad">Sync</span></Link>
          </div>
          <h1 className="db-auth-title">Create your account</h1>
          <p className="db-auth-sub">
            One account for the app and the web. In-person friend verification happens in the
            Android app — but you can join circles, RSVP, and follow along right here.
          </p>

          <label className="db-field">
            <span>Email</span>
            <input
              type="email" autoComplete="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </label>
          <label className="db-field">
            <span>Password</span>
            <div style={{ position: 'relative' }}>
              <input
                type={showPass ? 'text' : 'password'} autoComplete="new-password" required minLength={6}
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                style={{ width: '100%', paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                title={showPass ? 'Hide password' : 'Show password'}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16, padding: 4 }}
              >
                {showPass ? '🙈' : '👁'}
              </button>
            </div>
          </label>
          <label className="db-field">
            <span>Confirm password</span>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirm ? 'text' : 'password'} autoComplete="new-password" required minLength={6}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="Same password again"
                style={{ width: '100%', paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                title={showConfirm ? 'Hide password' : 'Show password'}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16, padding: 4 }}
              >
                {showConfirm ? '🙈' : '👁'}
              </button>
            </div>
          </label>
          <label className="db-field">
            <span>Birth year</span>
            <input
              type="text" inputMode="numeric" pattern="[0-9]{4}" maxLength={4} required
              value={birthYear}
              onChange={(e) => { setBirthYear(e.target.value.replace(/\D/g, '')); setNeedsGuardian(false); }}
              placeholder="YYYY"
            />
          </label>

          {error && <div className="db-auth-error">{error}</div>}
          {needsGuardian && (
            <div className="db-auth-notice">
              We can’t create this account on its own just yet — but a parent or guardian can add
              you as a managed member from their SphereSync account (Profile → Guardian), so you
              can still share moments with your circles.
            </div>
          )}
          {existing && (
            <Link
              to="/login"
              state={{ from: { pathname: dest } }}
              className="db-btn db-btn-block"
              style={{ textAlign: 'center', marginBottom: 10 }}
            >
              Sign in instead
            </Link>
          )}

          <button className="db-btn db-btn-primary db-btn-block" type="submit" disabled={busy}>
            {busy ? 'Creating account…' : 'Create account'}
          </button>

          <SocialAuth dest={dest} onError={setError} />

          <p className="db-auth-foot">
            By creating an account you agree to the <a href="/terms.html">Terms of Service</a> and{' '}
            <a href="/privacy.html">Privacy Policy</a>.
          </p>

          <p className="db-auth-foot">
            Already have an account?{' '}
            <Link to="/login" state={location.state} className="db-auth-link">Sign in</Link>
          </p>
        </form>
      )}
    </div>
  );
}
